import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/ui/Icon';
import Footer from '../homepage/components/Footer';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [sessionError, setSessionError] = useState('');

  useEffect(() => {
    let mounted = true;

    // Listen for auth state changes (handles the URL hash parsing automatically)
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session) {
        if (mounted) setSessionError('');
      }
    });

    const checkSession = async () => {
      // Small delay to allow Supabase to parse the URL hash on initial load
      setTimeout(async () => {
        if (!mounted) return;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setSessionError('Your password reset link is invalid or has expired. Please request a new one.');
        }
      }, 1000);
    };
    checkSession();

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFormError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setFormError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw updateError;
      }

      setMessage('Your password has been successfully reset! Redirecting to login...');
      setTimeout(() => {
        navigate('/signin');
      }, 3000);

    } catch (err) {
      console.error('Password reset error:', err);
      // If the token expired while they were typing or session was lost
      if (err.message?.toLowerCase().includes('session') || err.message?.toLowerCase().includes('expired') || err.status === 401) {
        setSessionError('Your password reset link is invalid or has expired. Please request a new one.');
      } else {
        setFormError(err.message || 'Failed to update password.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-8 text-center border-b border-gray-100">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Key" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Set New Password</h2>
            <p className="mt-2 text-sm text-gray-600">
              Please enter your new password below.
            </p>
          </div>

          <div className="p-8">
            {sessionError ? (
              <div className="text-center">
                <div className="p-4 bg-red-50 rounded-lg flex flex-col items-center space-y-3 mb-6">
                  <Icon name="AlertCircle" className="w-8 h-8 text-red-600" />
                  <p className="text-sm text-red-700">{sessionError}</p>
                </div>
                <Button onClick={() => navigate('/forgot-password')} className="w-full">
                  Request New Password Reset
                </Button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-6">
                {formError && (
                  <div className="p-4 bg-red-50 rounded-lg flex items-start space-x-3">
                    <Icon name="AlertCircle" className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}

                {message && (
                  <div className="p-4 bg-green-50 rounded-lg flex items-start space-x-3">
                    <Icon name="CheckCircle" className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-green-700">{message}</p>
                  </div>
                )}

                {!message && (
                  <>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-700">New Password</label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); setFormError(''); }}
                        placeholder="Enter new password"
                        required
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium text-gray-700">Confirm New Password</label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); setFormError(''); }}
                        placeholder="Confirm new password"
                        required
                        className="w-full"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading || !!formError}
                    >
                      {loading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </>
                )}
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
